/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useLazyQuery, useMutation } from '@apollo/client'
import { format as formatDate } from 'date-fns'
import React from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  BEGIN_REVIEW_EDIT_CORRECTION,
  BEGIN_REVIEW_EDIT_CURRENT_CLASSIFICATION,
  UNVERIFY_CLASSIFICATION_BATCH,
  UNVERIFY_CORRECTION,
  UNVERIFY_DETECTION,
} from '@dai/graphql'

import {
  BEGIN_REVIEW_EDIT_CLASSIFICATION,
  BEGIN_REVIEW_EDIT_DETECTION,
  BeginReviewEditClassificationMutation,
  BeginReviewEditClassificationMutationVariables,
  BeginReviewEditCorrectionMutation,
  BeginReviewEditCorrectionMutationVariables,
  BeginReviewEditDetectionMutation,
  BeginReviewEditDetectionMutationVariables,
  DetectionImageSession,
  REVIEW_OBJECT_ITEM,
  REVIEW_SEARCH_USER,
  REVIEW_USER_WORKFLOW_ITEM,
  ReviewItem,
  ReviewObjectItemQuery,
  ReviewObjectItemQueryVariables,
  ReviewSearchUserQuery,
  ReviewSearchUserQueryVariables,
  ReviewUserSearchResult,
  ReviewUserWorkflowItemQuery,
  ReviewUserWorkflowItemQueryVariables,
  ReviewWorkflow,
} from '@/graphql'
import useT from '@/i18n/useT'
import type {
  ClassificationBatch,
  LeaseLockError,
  NoItemsAvailableError,
} from '../classificationTypes'
import type { ClassificationCorrection } from '../correctionTypes'

import {
  buildReviewSearchParams,
  DEFAULT_REVIEW_WORKFLOW,
  parseReviewSearchParams,
  ReviewMode,
} from './reviewUrlState'

const SUPPORTED_REVIEW_WORKFLOWS: ReviewWorkflow[] = [
  'DETECTION',
  'CLASSIFICATION',
  'CORRECTION',
]

type BeginReviewEditCurrentClassificationMutation = {
  beginReviewEditCurrentClassification:
    | ClassificationBatch
    | LeaseLockError
    | NoItemsAvailableError
    | null
}

type BeginReviewEditCurrentClassificationMutationVariables = {
  boxId: string
}

type OperationMessage = {
  message: string
}

type UnverifyDetectionMutation = {
  unverifyImageDetections:
    | {
        __typename: 'VerifyImageDetectionsResult'
        imageId: string
        success: boolean
      }
    | {
        __typename: 'OperationInfo'
        messages: OperationMessage[]
      }
    | null
}

type UnverifyDetectionMutationVariables = {
  imageId: string
  feedbackNote?: string | null
}

type ReviewUnverifyMutationResult = {
  __typename: 'ReviewUnverifyResult'
  success: boolean
}

type ReviewUnverifyMutationError = {
  __typename: 'LeaseLockError' | 'NoItemsAvailableError'
  message: string | null
}

type UnverifyClassificationBatchMutation = {
  unverifyClassificationBatch:
    | ReviewUnverifyMutationResult
    | ReviewUnverifyMutationError
    | null
}

type UnverifyClassificationBatchMutationVariables = {
  batchId: string
  feedbackNote?: string | null
}

type UnverifyCorrectionMutation = {
  unverifyCorrection:
    | ReviewUnverifyMutationResult
    | ReviewUnverifyMutationError
    | null
}

type UnverifyCorrectionMutationVariables = {
  boxId: string
  feedbackNote?: string | null
}

type ReviewDateFilters = {
  startDate: string | null
  endDate: string | null
}

type ActiveReviewEdit =
  | {
      item: ReviewItem
      workflow: 'DETECTION'
      session: DetectionImageSession
    }
  | {
      item: ReviewItem
      workflow: 'CLASSIFICATION'
      batch: ClassificationBatch
    }
  | {
      item: ReviewItem
      workflow: 'CORRECTION'
      correction: ClassificationCorrection
    }

const formatReviewDate = (value: Date | null) =>
  value ? formatDate(value, 'yyyy-MM-dd') : null

const parseLocalDateFromYYYYMMDD = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null

  const [, year, month, day] = match
  return new Date(Number(year), Number(month) - 1, Number(day))
}

const isReviewItemShape = (value: unknown): value is ReviewItem => {
  if (!value || typeof value !== 'object') return false

  const candidate = value as {
    __typename?: string
    session?: unknown
    batch?: unknown
    correction?: unknown
  }

  if (candidate.__typename === 'DetectionReviewItem') {
    return typeof candidate.session === 'object' && candidate.session !== null
  }

  if (candidate.__typename === 'ClassificationReviewItem') {
    return typeof candidate.batch === 'object' && candidate.batch !== null
  }

  if (candidate.__typename === 'CorrectionReviewItem') {
    return (
      typeof candidate.correction === 'object' && candidate.correction !== null
    )
  }

  return false
}

const isMutationErrorPayload = (
  payload: { __typename?: string; message?: string | null } | null | undefined,
): payload is {
  __typename: 'LeaseLockError' | 'NoItemsAvailableError'
  message: string | null
} =>
  payload?.__typename === 'LeaseLockError' ||
  payload?.__typename === 'NoItemsAvailableError'

export function useReviewController() {
  const { t } = useT()
  const [searchParams, setSearchParams] = useSearchParams()
  const [mode, setMode] = React.useState<ReviewMode>('BY_USER')
  const [email, setEmail] = React.useState('')
  const [objectId, setObjectId] = React.useState('')
  const [userWorkflow, setUserWorkflow] = React.useState<ReviewWorkflow>(
    DEFAULT_REVIEW_WORKFLOW,
  )
  const [objectWorkflow, setObjectWorkflow] = React.useState<ReviewWorkflow>(
    DEFAULT_REVIEW_WORKFLOW,
  )
  const [activeUser, setActiveUser] =
    React.useState<ReviewUserSearchResult | null>(null)
  const [userItem, setUserItem] = React.useState<ReviewItem | null>(null)
  const [objectItem, setObjectItem] = React.useState<ReviewItem | null>(null)
  const [startDate, setStartDate] = React.useState<Date | null>(null)
  const [endDate, setEndDate] = React.useState<Date | null>(null)
  const [infoMessage, setInfoMessage] = React.useState<string | null>(null)
  const [snackbarMessage, setSnackbarMessage] = React.useState('')
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<
    'success' | 'warning'
  >('warning')
  const [activeReviewEdit, setActiveReviewEdit] =
    React.useState<ActiveReviewEdit | null>(null)
  const [filteredWorkflowCounts, setFilteredWorkflowCounts] = React.useState<
    Partial<Record<ReviewWorkflow, number>> | undefined
  >(undefined)
  const [loadingUserWorkflowRequest, setLoadingUserWorkflowRequest] =
    React.useState(false)
  const hydratedFromUrlRef = React.useRef(false)
  const requestTokenRef = React.useRef(0)
  const [hasHydratedFromUrl, setHasHydratedFromUrl] = React.useState(false)

  const [searchUser, searchUserState] = useLazyQuery<
    ReviewSearchUserQuery,
    ReviewSearchUserQueryVariables
  >(REVIEW_SEARCH_USER, {
    context: { endPoint: 'mlUltra' },
    fetchPolicy: 'network-only',
  })

  const userWorkflowItemQueryOptions = {
    context: { endPoint: 'mlUltra' },
    fetchPolicy: 'network-only' as const,
  }

  const [loadDetectionItem, detectionItemState] = useLazyQuery<
    ReviewUserWorkflowItemQuery,
    ReviewUserWorkflowItemQueryVariables
  >(REVIEW_USER_WORKFLOW_ITEM, userWorkflowItemQueryOptions)

  const [loadClassificationItem, classificationItemState] = useLazyQuery<
    ReviewUserWorkflowItemQuery,
    ReviewUserWorkflowItemQueryVariables
  >(REVIEW_USER_WORKFLOW_ITEM, userWorkflowItemQueryOptions)

  const [loadCorrectionItem, correctionItemState] = useLazyQuery<
    ReviewUserWorkflowItemQuery,
    ReviewUserWorkflowItemQueryVariables
  >(REVIEW_USER_WORKFLOW_ITEM, userWorkflowItemQueryOptions)

  const [loadObjectItem, objectItemState] = useLazyQuery<
    ReviewObjectItemQuery,
    ReviewObjectItemQueryVariables
  >(REVIEW_OBJECT_ITEM, {
    context: { endPoint: 'mlUltra' },
    fetchPolicy: 'network-only',
  })

  const [beginEditDetection, beginEditDetectionState] = useMutation<
    BeginReviewEditDetectionMutation,
    BeginReviewEditDetectionMutationVariables
  >(BEGIN_REVIEW_EDIT_DETECTION, {
    context: { endPoint: 'mlUltra' },
  })

  const [beginEditClassification, beginEditClassificationState] = useMutation<
    BeginReviewEditClassificationMutation,
    BeginReviewEditClassificationMutationVariables
  >(BEGIN_REVIEW_EDIT_CLASSIFICATION, {
    context: { endPoint: 'mlUltra' },
  })

  const [beginEditCurrentClassification, beginEditCurrentClassificationState] =
    useMutation<
      BeginReviewEditCurrentClassificationMutation,
      BeginReviewEditCurrentClassificationMutationVariables
    >(BEGIN_REVIEW_EDIT_CURRENT_CLASSIFICATION, {
      context: { endPoint: 'mlUltra' },
    })
  const [beginEditCorrection, beginEditCorrectionState] = useMutation<
    BeginReviewEditCorrectionMutation,
    BeginReviewEditCorrectionMutationVariables
  >(BEGIN_REVIEW_EDIT_CORRECTION, {
    context: { endPoint: 'mlUltra' },
  })

  const [unverifyDetection, unverifyDetectionState] = useMutation<
    UnverifyDetectionMutation,
    UnverifyDetectionMutationVariables
  >(UNVERIFY_DETECTION, {
    context: { endPoint: 'mlUltra' },
  })
  const [unverifyClassificationBatch, unverifyClassificationBatchState] =
    useMutation<
      UnverifyClassificationBatchMutation,
      UnverifyClassificationBatchMutationVariables
    >(UNVERIFY_CLASSIFICATION_BATCH, {
      context: { endPoint: 'mlUltra' },
    })
  const [unverifyCorrection, unverifyCorrectionState] = useMutation<
    UnverifyCorrectionMutation,
    UnverifyCorrectionMutationVariables
  >(UNVERIFY_CORRECTION, {
    context: { endPoint: 'mlUltra' },
  })
  const loadingUnverify =
    unverifyDetectionState.loading ||
    unverifyClassificationBatchState.loading ||
    unverifyCorrectionState.loading

  const activeItem = mode === 'BY_USER' ? userItem : objectItem
  const activeWorkflow = mode === 'BY_USER' ? userWorkflow : objectWorkflow
  const activeItemMetadata = isReviewItemShape(activeItem)
    ? activeItem.reviewMetadata
    : null
  const hasActiveReview = Boolean(activeItem) || Boolean(activeUser)
  const resolvedObjectId = activeItemMetadata?.objectId || objectId.trim()
  const loadingEdit =
    beginEditDetectionState.loading ||
    beginEditClassificationState.loading ||
    beginEditCurrentClassificationState.loading ||
    beginEditCorrectionState.loading
  const workflowItemLoaders = {
    DETECTION: loadDetectionItem,
    CLASSIFICATION: loadClassificationItem,
    CORRECTION: loadCorrectionItem,
  } as const

  const workflowItemStates = {
    DETECTION: detectionItemState,
    CLASSIFICATION: classificationItemState,
    CORRECTION: correctionItemState,
  } as const

  const activeUserItemState =
    workflowItemStates[userWorkflow as keyof typeof workflowItemStates] ??
    detectionItemState

  const anyWorkflowItemLoading =
    detectionItemState.loading ||
    classificationItemState.loading ||
    correctionItemState.loading

  const loadingInitialUserSearch =
    mode === 'BY_USER' &&
    !activeUser &&
    (searchUserState.loading || anyWorkflowItemLoading)

  const loadingActiveUserWorkflow =
    mode === 'BY_USER' && Boolean(activeUser) && loadingUserWorkflowRequest

  const workflowCounts = React.useMemo(() => {
    if (filteredWorkflowCounts) return filteredWorkflowCounts
    if (!activeUser) return undefined
    return activeUser.workflowSummaries.reduce(
      (acc, summary) => ({
        ...acc,
        [summary.workflow]: summary.totalCount,
      }),
      {} as Partial<Record<ReviewWorkflow, number>>,
    )
  }, [activeUser, filteredWorkflowCounts])

  const workflowDisabled = React.useMemo(() => {
    if (!workflowCounts) return undefined
    return {
      DETECTION:
        userWorkflow === 'DETECTION'
          ? false
          : (workflowCounts.DETECTION ?? 0) === 0,
      CLASSIFICATION:
        userWorkflow === 'CLASSIFICATION'
          ? false
          : (workflowCounts.CLASSIFICATION ?? 0) === 0,
      CORRECTION: (workflowCounts.CORRECTION ?? 0) === 0,
    } satisfies Partial<Record<ReviewWorkflow, boolean>>
  }, [userWorkflow, workflowCounts])

  const getPreferredWorkflow = React.useCallback(
    (
      workflowSummaries: ReviewUserSearchResult['workflowSummaries'],
      preferredWorkflow?: ReviewWorkflow,
    ) => {
      if (
        preferredWorkflow &&
        SUPPORTED_REVIEW_WORKFLOWS.includes(preferredWorkflow)
      ) {
        return preferredWorkflow
      }

      return (
        workflowSummaries.find(
          summary =>
            SUPPORTED_REVIEW_WORKFLOWS.includes(summary.workflow) &&
            summary.totalCount > 0,
        )?.workflow ?? null
      )
    },
    [],
  )

  const clearTargetItem = React.useCallback((target: 'USER' | 'OBJECT') => {
    setActiveReviewEdit(null)
    if (target === 'USER') setUserItem(null)
    else setObjectItem(null)
  }, [])

  const setTargetItem = React.useCallback(
    (target: 'USER' | 'OBJECT', item: ReviewItem) => {
      setInfoMessage(null)
      if (target === 'USER') setUserItem(item)
      else setObjectItem(item)
    },
    [],
  )

  const setItemFromPayload = React.useCallback(
    (
      payload:
        | ReviewUserWorkflowItemQuery['reviewUserWorkflowItem']
        | ReviewObjectItemQuery['reviewObjectItem']
        | null,
      target: 'USER' | 'OBJECT',
      workflow: ReviewWorkflow,
    ) => {
      if (!payload) {
        clearTargetItem(target)
        return
      }

      if (payload.__typename === 'NoItemsAvailableError') {
        clearTargetItem(target)
        const message =
          target === 'OBJECT' && workflow === 'CLASSIFICATION'
            ? t(
                'review.errors.classificationBatchNotFound',
                'No classification batch was found for that ID.',
              )
            : target === 'OBJECT' && workflow === 'CORRECTION'
              ? t(
                  'review.errors.correctionBoxNotFound',
                  'No correction box was found for that ID.',
                )
              : payload.message
        setInfoMessage(message)
        return
      }

      if (!isReviewItemShape(payload)) {
        console.error('Unexpected review item payload shape', payload)
        clearTargetItem(target)
        setInfoMessage(
          t(
            'review.errors.invalidPayload',
            'The review item response was missing required fields.',
          ),
        )
        return
      }

      setTargetItem(target, payload)
    },
    [clearTargetItem, setTargetItem, t],
  )

  const getWorkflowCountFromPayload = React.useCallback((payload: unknown) => {
    if (!payload || typeof payload !== 'object') return 0

    const candidate = payload as {
      __typename?: string
      pager?: { totalCount?: unknown }
    }

    if (candidate.__typename === 'NoItemsAvailableError') {
      return 0
    }

    return typeof candidate.pager?.totalCount === 'number'
      ? candidate.pager.totalCount
      : 0
  }, [])

  const syncWorkflowCountFromPayload = React.useCallback(
    (
      workflow: ReviewWorkflow,
      payload:
        | ReviewUserWorkflowItemQuery['reviewUserWorkflowItem']
        | ReviewObjectItemQuery['reviewObjectItem']
        | null,
    ) => {
      const nextCount = getWorkflowCountFromPayload(payload)
      setFilteredWorkflowCounts(prev => ({
        DETECTION:
          prev?.DETECTION ??
          activeUser?.workflowSummaries.find(
            summary => summary.workflow === 'DETECTION',
          )?.totalCount ??
          0,
        CLASSIFICATION:
          prev?.CLASSIFICATION ??
          activeUser?.workflowSummaries.find(
            summary => summary.workflow === 'CLASSIFICATION',
          )?.totalCount ??
          0,
        CORRECTION:
          prev?.CORRECTION ??
          activeUser?.workflowSummaries.find(
            summary => summary.workflow === 'CORRECTION',
          )?.totalCount ??
          0,
        [workflow]: nextCount,
      }))
    },
    [activeUser?.workflowSummaries, getWorkflowCountFromPayload],
  )

  const loadUserItemAtIndex = React.useCallback(
    async (workflow: ReviewWorkflow, index = 1, userEmail?: string) => {
      const resolvedEmail = (userEmail ?? activeUser?.email ?? '').trim()
      if (!resolvedEmail) return
      const requestToken = ++requestTokenRef.current
      setLoadingUserWorkflowRequest(true)

      const loader =
        workflowItemLoaders[workflow as keyof typeof workflowItemLoaders] ??
        loadDetectionItem

      try {
        const response = await loader({
          variables: {
            email: resolvedEmail,
            workflow,
            startDate: formatReviewDate(startDate),
            endDate: formatReviewDate(endDate),
            index,
          },
        })
        if (requestToken !== requestTokenRef.current) return

        syncWorkflowCountFromPayload(
          workflow,
          response.data?.reviewUserWorkflowItem ?? null,
        )
        setItemFromPayload(
          response.data?.reviewUserWorkflowItem ?? null,
          'USER',
          workflow,
        )
      } finally {
        if (requestToken === requestTokenRef.current) {
          setLoadingUserWorkflowRequest(false)
        }
      }
    },
    [
      activeUser?.email,
      endDate,
      loadCorrectionItem,
      loadDetectionItem,
      loadClassificationItem,
      setItemFromPayload,
      startDate,
      syncWorkflowCountFromPayload,
      workflowItemLoaders,
    ],
  )

  const refreshUserWorkflowOverview = React.useCallback(
    async (
      workflow: ReviewWorkflow,
      userEmail?: string,
      index = 1,
      filters?: ReviewDateFilters,
    ) => {
      const resolvedEmail = (userEmail ?? activeUser?.email ?? '').trim()
      if (!resolvedEmail) return

      const requestToken = ++requestTokenRef.current
      setLoadingUserWorkflowRequest(true)

      const makeVariables = (wf: ReviewWorkflow, idx: number) => ({
        variables: {
          email: resolvedEmail,
          workflow: wf,
          startDate: filters?.startDate ?? formatReviewDate(startDate),
          endDate: filters?.endDate ?? formatReviewDate(endDate),
          index: idx,
        },
      })

      const activeLoader =
        workflowItemLoaders[workflow as keyof typeof workflowItemLoaders] ??
        loadDetectionItem

      const otherWorkflows = SUPPORTED_REVIEW_WORKFLOWS.filter(
        candidate => candidate !== workflow,
      )

      try {
        const [activeResponse, ...otherResponses] = await Promise.all([
          activeLoader(makeVariables(workflow, index)),
          ...otherWorkflows.map(candidate => {
            const loader =
              workflowItemLoaders[
                candidate as keyof typeof workflowItemLoaders
              ] ?? loadDetectionItem
            return loader(makeVariables(candidate, 1))
          }),
        ])

        if (requestToken !== requestTokenRef.current) return

        const counts: Partial<Record<ReviewWorkflow, number>> = {
          DETECTION: 0,
          CLASSIFICATION: 0,
          CORRECTION: 0,
        }

        counts[workflow] = getWorkflowCountFromPayload(
          activeResponse.data?.reviewUserWorkflowItem,
        )

        otherWorkflows.forEach((candidate, i) => {
          counts[candidate] = getWorkflowCountFromPayload(
            otherResponses[i]?.data?.reviewUserWorkflowItem,
          )
        })

        setFilteredWorkflowCounts(counts)
        setItemFromPayload(
          activeResponse.data?.reviewUserWorkflowItem ?? null,
          'USER',
          workflow,
        )
      } finally {
        if (requestToken === requestTokenRef.current) {
          setLoadingUserWorkflowRequest(false)
        }
      }
    },
    [
      activeUser?.email,
      endDate,
      getWorkflowCountFromPayload,
      loadCorrectionItem,
      loadDetectionItem,
      loadClassificationItem,
      setItemFromPayload,
      startDate,
      workflowItemLoaders,
    ],
  )

  const handleUserSearch = React.useCallback(async () => {
    const normalizedEmail = email.trim()
    if (!normalizedEmail) return
    const requestToken = ++requestTokenRef.current

    const response = await searchUser({ variables: { email: normalizedEmail } })
    if (requestToken !== requestTokenRef.current) return
    const payload = response.data?.reviewSearchUser

    if (!payload) return
    if (payload.__typename === 'NoItemsAvailableError') {
      setActiveUser(null)
      setFilteredWorkflowCounts(undefined)
      clearTargetItem('USER')
      setInfoMessage(payload.message)
      return
    }

    const nextWorkflow = getPreferredWorkflow(
      payload.workflowSummaries,
      userWorkflow,
    )

    if (!nextWorkflow) {
      setActiveUser(null)
      clearTargetItem('USER')
      setInfoMessage(
        t(
          'review.search.noSupportedWorkflowItems',
          'No supported review items were found for detection, classification, or correction.',
        ),
      )
      return
    }

    clearTargetItem('USER')
    setActiveUser(payload)
    setFilteredWorkflowCounts(undefined)
    setInfoMessage(null)
    setUserWorkflow(nextWorkflow)
    if (requestToken !== requestTokenRef.current) return
    await refreshUserWorkflowOverview(nextWorkflow, payload.email)
  }, [
    clearTargetItem,
    email,
    getPreferredWorkflow,
    refreshUserWorkflowOverview,
    searchUser,
    t,
    userWorkflow,
  ])

  const handleObjectSearch = React.useCallback(async () => {
    const normalizedObjectId = objectId.trim()
    if (!normalizedObjectId) return
    const requestToken = ++requestTokenRef.current

    const response = await loadObjectItem({
      variables: {
        workflow: objectWorkflow,
        objectId: normalizedObjectId,
      },
    })
    if (requestToken !== requestTokenRef.current) return

    setItemFromPayload(
      response.data?.reviewObjectItem ?? null,
      'OBJECT',
      objectWorkflow,
    )
  }, [loadObjectItem, objectId, objectWorkflow, setItemFromPayload])

  const handleModeChange = React.useCallback(
    (_: React.SyntheticEvent, nextMode: ReviewMode) => {
      if (nextMode === mode) return

      if (nextMode === 'BY_OBJECT') {
        setObjectWorkflow(userWorkflow)
      } else {
        setUserWorkflow(objectWorkflow)
      }

      setMode(nextMode)
    },
    [mode, objectWorkflow, userWorkflow],
  )

  const handleUserWorkflowChange = React.useCallback(
    async (workflow: ReviewWorkflow) => {
      setUserWorkflow(workflow)
      clearTargetItem('USER')
      setInfoMessage(null)
      await loadUserItemAtIndex(workflow, 1)
    },
    [clearTargetItem, loadUserItemAtIndex],
  )

  const handleApplyFilters = React.useCallback(async () => {
    clearTargetItem('USER')
    setInfoMessage(null)
    await refreshUserWorkflowOverview(userWorkflow)
  }, [clearTargetItem, refreshUserWorkflowOverview, userWorkflow])

  const handlePreviousUserItem = React.useCallback(async () => {
    if (!userItem?.pager.hasPrevious) return
    await loadUserItemAtIndex(userWorkflow, userItem.pager.currentIndex - 1)
  }, [loadUserItemAtIndex, userItem, userWorkflow])

  const handleNextUserItem = React.useCallback(async () => {
    if (!userItem?.pager.hasNext) return
    await loadUserItemAtIndex(userWorkflow, userItem.pager.currentIndex + 1)
  }, [loadUserItemAtIndex, userItem, userWorkflow])

  const handleMutationError = React.useCallback((message: string | null) => {
    if (message) {
      setSnackbarSeverity('warning')
      setSnackbarMessage(message)
    }
  }, [])

  const handleCancelReviewEdit = React.useCallback(() => {
    setActiveReviewEdit(null)
  }, [])

  const reloadActiveItem = React.useCallback(
    async (item: ReviewItem) => {
      if (mode === 'BY_USER' && activeUser) {
        await loadUserItemAtIndex(
          userWorkflow,
          userItem?.pager.currentIndex ?? 1,
        )
      } else {
        const workflow = item.reviewMetadata.workflow
        const requestToken = requestTokenRef.current
        const resp = await loadObjectItem({
          variables: { workflow, objectId: item.reviewMetadata.objectId },
        })
        if (requestToken !== requestTokenRef.current) return
        setItemFromPayload(
          resp.data?.reviewObjectItem ?? null,
          'OBJECT',
          workflow,
        )
      }
    },
    [
      activeUser,
      loadObjectItem,
      loadUserItemAtIndex,
      mode,
      setItemFromPayload,
      userItem?.pager.currentIndex,
      userWorkflow,
    ],
  )

  const handleUnverify = React.useCallback(
    async (
      item: ReviewItem,
      feedbackNote?: string | null,
    ): Promise<boolean> => {
      if (item.__typename === 'DetectionReviewItem') {
        let response
        try {
          response = await unverifyDetection({
            variables: { imageId: item.session.image.id, feedbackNote },
          })
        } catch (err) {
          handleMutationError(
            err instanceof Error ? err.message : 'Failed to unverify.',
          )
          return false
        }
        const payload = response.data?.unverifyImageDetections
        if (payload?.__typename === 'OperationInfo') {
          handleMutationError(
            payload.messages.map(m => m.message).join(' ') ||
              'Failed to unverify.',
          )
          return false
        }
        if (payload?.__typename === 'VerifyImageDetectionsResult') {
          if (!payload.success) {
            handleMutationError('Failed to unverify.')
            return false
          }
          await reloadActiveItem(item)
          return true
        }
        return false
      }
      if (item.__typename === 'ClassificationReviewItem') {
        if (!item.classificationReviewBatchId) {
          handleMutationError('Failed to unverify.')
          return false
        }

        try {
          const response = await unverifyClassificationBatch({
            variables: {
              batchId: item.classificationReviewBatchId,
              feedbackNote,
            },
          })
          const payload = response.data?.unverifyClassificationBatch
          if (isMutationErrorPayload(payload)) {
            handleMutationError(payload.message ?? 'Failed to unverify.')
            return false
          }
          if (!payload?.success) {
            handleMutationError('Failed to unverify.')
            return false
          }
          await reloadActiveItem(item)
          return true
        } catch (err) {
          handleMutationError(
            err instanceof Error ? err.message : 'Failed to unverify.',
          )
          return false
        }
      }

      if (item.__typename === 'CorrectionReviewItem') {
        try {
          const response = await unverifyCorrection({
            variables: {
              boxId: item.reviewMetadata.objectId,
              feedbackNote,
            },
          })
          const payload = response.data?.unverifyCorrection
          if (isMutationErrorPayload(payload)) {
            handleMutationError(payload.message ?? 'Failed to unverify.')
            return false
          }
          if (!payload?.success) {
            handleMutationError('Failed to unverify.')
            return false
          }
          await reloadActiveItem(item)
          return true
        } catch (err) {
          handleMutationError(
            err instanceof Error ? err.message : 'Failed to unverify.',
          )
          return false
        }
      }

      return false
    },
    [
      handleMutationError,
      reloadActiveItem,
      unverifyDetection,
      unverifyClassificationBatch,
      unverifyCorrection,
    ],
  )

  const handleEdit = React.useCallback(
    async (item: ReviewItem) => {
      if (!item.reviewMetadata.canEnterEditMode) return

      if (item.__typename === 'DetectionReviewItem') {
        const response = await beginEditDetection({
          variables: { imageId: item.session.image.id },
        })
        const payload = response.data?.beginReviewEditDetection
        if (isMutationErrorPayload(payload)) {
          handleMutationError(payload.message ?? null)
          return
        }
        if (payload?.__typename === 'DetectionImageSession') {
          setActiveReviewEdit({
            item,
            workflow: 'DETECTION',
            session: payload,
          })
        }
        return
      }

      if (item.__typename === 'CorrectionReviewItem') {
        const response = await beginEditCorrection({
          variables: { boxId: item.reviewMetadata.objectId },
        })
        const payload = response.data?.beginReviewEditCorrection

        if (isMutationErrorPayload(payload)) {
          handleMutationError(payload.message ?? null)
          return
        }

        if (payload?.__typename === 'ClassificationCorrection') {
          setActiveReviewEdit({
            item,
            workflow: 'CORRECTION',
            correction: payload,
          })
        }
        return
      }

      if (item.__typename !== 'ClassificationReviewItem') {
        return
      }

      if (item.classificationReviewBatchId) {
        const response = await beginEditClassification({
          variables: { batchId: item.classificationReviewBatchId },
        })
        const payload = response.data?.beginReviewEditClassification

        if (isMutationErrorPayload(payload)) {
          handleMutationError(payload.message ?? null)
          return
        }

        if (payload?.__typename === 'ClassificationBatch') {
          setActiveReviewEdit({
            item,
            workflow: 'CLASSIFICATION',
            batch: payload,
          })
        }
        return
      }

      const response = await beginEditCurrentClassification({
        variables: { boxId: item.reviewMetadata.objectId },
      })
      const payload = response.data?.beginReviewEditCurrentClassification

      if (isMutationErrorPayload(payload)) {
        handleMutationError(payload.message ?? null)
        return
      }

      if (payload?.__typename === 'ClassificationBatch') {
        setActiveReviewEdit({
          item,
          workflow: 'CLASSIFICATION',
          batch: payload,
        })
      }
    },
    [
      beginEditClassification,
      beginEditCorrection,
      beginEditCurrentClassification,
      beginEditDetection,
      handleMutationError,
    ],
  )

  const handleReviewEditSaved = React.useCallback(async () => {
    const editedItem = activeReviewEdit?.item
    setActiveReviewEdit(null)
    setSnackbarSeverity('success')
    setSnackbarMessage(t('review.edit.savedToast', 'Saved review edit.'))
    if (editedItem) {
      try {
        await reloadActiveItem(editedItem)
      } catch (reloadError) {
        console.error('Failed to refresh review item after edit', reloadError)
        setSnackbarSeverity('warning')
        setSnackbarMessage(
          t(
            'review.edit.savedRefreshFailure',
            'Saved review edit, but failed to refresh the review item.',
          ),
        )
      }
    }
  }, [activeReviewEdit?.item, reloadActiveItem, t])

  const handleObjectWorkflowChange = React.useCallback(
    (workflow: ReviewWorkflow) => {
      setObjectWorkflow(workflow)
      clearTargetItem('OBJECT')
      setInfoMessage(null)
    },
    [clearTargetItem],
  )

  const handleResetReview = React.useCallback(() => {
    setActiveReviewEdit(null)
    setActiveUser(null)
    setFilteredWorkflowCounts(undefined)
    clearTargetItem('USER')
    clearTargetItem('OBJECT')
    setEmail('')
    setObjectId('')
    setInfoMessage(null)
    setStartDate(null)
    setEndDate(null)
    setSearchParams(
      buildReviewSearchParams({
        email: '',
        mode,
        objectId: '',
        startDate: null,
        endDate: null,
        workflow: activeWorkflow,
      }),
      { replace: true },
    )
  }, [activeWorkflow, clearTargetItem, mode, setSearchParams])

  React.useEffect(() => {
    if (hydratedFromUrlRef.current) return
    hydratedFromUrlRef.current = true

    const parsedState = parseReviewSearchParams(searchParams)

    if (parsedState.startDate) {
      setStartDate(parseLocalDateFromYYYYMMDD(parsedState.startDate))
    }
    if (parsedState.endDate) {
      setEndDate(parseLocalDateFromYYYYMMDD(parsedState.endDate))
    }

    if (parsedState.mode === 'BY_OBJECT') {
      setMode('BY_OBJECT')
      setObjectWorkflow(parsedState.workflow)
      if (parsedState.objectId) {
        setObjectId(parsedState.objectId)
        void loadObjectItem({
          variables: {
            workflow: parsedState.workflow,
            objectId: parsedState.objectId,
          },
        }).then(response => {
          setItemFromPayload(
            response.data?.reviewObjectItem ?? null,
            'OBJECT',
            parsedState.workflow,
          )
        })
      }
      setHasHydratedFromUrl(true)
      return
    }

    setMode('BY_USER')
    setUserWorkflow(parsedState.workflow)
    if (parsedState.email) {
      setEmail(parsedState.email)
      void searchUser({ variables: { email: parsedState.email } }).then(
        response => {
          const payload = response.data?.reviewSearchUser
          if (!payload || payload.__typename === 'NoItemsAvailableError') {
            return
          }
          const nextWorkflow = parsedState.workflow
          if (!SUPPORTED_REVIEW_WORKFLOWS.includes(nextWorkflow)) {
            return
          }
          const hasSupportedItems = payload.workflowSummaries.some(
            summary =>
              SUPPORTED_REVIEW_WORKFLOWS.includes(summary.workflow) &&
              summary.totalCount > 0,
          )
          if (!hasSupportedItems) {
            setInfoMessage(
              t(
                'review.search.noSupportedWorkflowItems',
                'No supported review items were found for detection, classification, or correction.',
              ),
            )
            return
          }
          setActiveUser(payload)
          setUserWorkflow(nextWorkflow)
          void refreshUserWorkflowOverview(
            nextWorkflow,
            parsedState.email!,
            parsedState.index,
            {
              startDate: parsedState.startDate,
              endDate: parsedState.endDate,
            },
          )
        },
      )
    }
    setHasHydratedFromUrl(true)
  }, [loadObjectItem, refreshUserWorkflowOverview, searchParams, searchUser, t])

  React.useEffect(() => {
    if (!hasHydratedFromUrl) return

    const next = buildReviewSearchParams({
      activeUserEmail: activeUser?.email,
      email,
      itemIndex: userItem?.pager.currentIndex,
      mode,
      objectId: resolvedObjectId,
      startDate: formatReviewDate(startDate),
      endDate: formatReviewDate(endDate),
      workflow: activeWorkflow,
    })

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
  }, [
    activeUser?.email,
    activeWorkflow,
    email,
    endDate,
    hasHydratedFromUrl,
    mode,
    resolvedObjectId,
    searchParams,
    setSearchParams,
    startDate,
    userItem,
  ])

  return {
    activeItem,
    activeItemMetadata,
    activeReviewEdit,
    activeUser,
    activeWorkflow,
    email,
    endDate,
    hasActiveReview,
    handleApplyFilters,
    handleEdit,
    handleCancelReviewEdit,
    handleModeChange,
    handleNextUserItem,
    handleObjectSearch,
    handleObjectWorkflowChange,
    handlePreviousUserItem,
    handleResetReview,
    handleUnverify,
    handleReviewEditSaved,
    handleUserSearch,
    handleUserWorkflowChange,
    infoMessage,
    loadingEdit,
    loadingUnverify,
    loadingActiveUserWorkflow,
    loadingInitialUserSearch,
    loadingObjectSearch: objectItemState.loading,
    loadingUserSearch:
      searchUserState.loading ||
      anyWorkflowItemLoading ||
      loadingUserWorkflowRequest,
    mode,
    objectId,
    setEmail,
    setEndDate,
    setObjectId,
    setStartDate,
    snackbarMessage,
    snackbarSeverity,
    setSnackbarMessage,
    startDate,
    userItem,
    userItemState: activeUserItemState,
    userWorkflow,
    workflowCounts,
    workflowDisabled,
    queryError:
      searchUserState.error ||
      activeUserItemState.error ||
      objectItemState.error,
  }
}
